/**
 * parseTenantCssAssets
 *
 * Two complementary strategies for applying tenant CDN assets:
 *
 * ── Strategy A: resolveImageVarsFromCascade (PRIMARY, always runs)  ──────────
 *   CORS-free.  After the tenant.css <link> is in the DOM, the browser has
 *   already applied its CSS variable values via the normal stylesheet cascade.
 *   We read each --image-* variable from getComputedStyle, resolve any relative
 *   url() reference to an ABSOLUTE URL using the tenant.css href as the base
 *   URL (exactly as the CSS spec intends), then write the result back as an
 *   inline style so the browser fetches the image from the correct CDN origin.
 *
 *   Why inline style?  background-image: var(--image-banner-1) in an emotion
 *   <style> block resolves the url() relative to the DOCUMENT origin, not the
 *   tenant.css origin.  Re-applying the variable as an absolute-URL inline
 *   style is the only reliable cross-browser fix without touching tenant.css.
 *
 * ── Strategy B: parseTenantCssAssets (SECONDARY, best-effort, async) ─────────
 *   Fetches the CSS text to extract @font-face blocks and re-inject them with
 *   absolute src URLs (needed when the font file paths in tenant.css are
 *   relative and would otherwise resolve to the wrong origin when injected as a
 *   <style> element on the page).  Silently no-ops on CORS/network failure
 *   because the browser already loads @font-face from the linked <link>.
 */

/** Resolve relative url(...) values to absolute URLs using baseHref as origin.
 *  Leaves already-absolute URLs (http/https/data/protocol-relative) AND
 *  root-relative paths (starting with /) untouched. */
function resolveUrls(cssText: string, baseHref: string): string {
  let base: URL;
  try {
    base = new URL(baseHref);
  } catch {
    return cssText;
  }

  return cssText.replace(
    /url\(\s*(["']?)([^"')]+)\1\s*\)/g,
    (_match: string, quote: string, rawUrl: string) => {
      const trimmed = rawUrl.trim();
      if (
        trimmed.startsWith("http://") ||
        trimmed.startsWith("https://") ||
        trimmed.startsWith("//")       ||
        trimmed.startsWith("data:")    ||
        trimmed.startsWith("/")          // root-relative path — keep as-is
      ) {
        return _match;
      }
      try {
        const absolute = new URL(trimmed, base).href;
        return `url(${quote}${absolute}${quote})`;
      } catch {
        return _match;
      }
    }
  );
}

/** --image-* CSS variable names managed by injectAssetVars. */
const IMAGE_VAR_NAMES = [
  "--image-logo",
  "--image-logo-svg",
  "--image-banner-1",
  "--image-banner-2",
  "--image-bg-watermark",
  "--image-favicon",
  "--image-flag-en",
  "--image-flag-es",
  "--image-app-icon",
  "--image-avatar-placeholder",
];

/**
 * Strategy A — CORS-free image URL resolution.
 *
 * Reads every --image-* CSS variable from the computed cascade (no fetch
 * required — the browser already parsed tenant.css as a linked stylesheet),
 * resolves any relative url() value to an absolute URL using cssHref as the
 * base, and applies the result as an inline style on <html>.
 *
 * This correctly handles tenant.css entries like:
 *   --image-banner-1: url("asset/banner-1.png");
 * which are relative to the stylesheet origin (e.g. an S3 bucket path) — NOT
 * to the page origin — and would otherwise resolve to the wrong location when
 * the browser substitutes the var() inside an emotion <style> block.
 *
 * Only variables whose url() value is genuinely relative are modified;
 * root-relative (/…) and already-absolute (https://…) values are left alone.
 */
export function resolveImageVarsFromCascade(cssHref: string): void {
  if (typeof document === "undefined") return;

  const root    = document.documentElement;
  const cascade = getComputedStyle(root);

  for (const varName of IMAGE_VAR_NAMES) {
    const raw = cascade.getPropertyValue(varName).trim();
    if (!raw) continue;

    const resolved = resolveUrls(raw, cssHref);
    // Only promote to inline style when the URL was actually changed
    // (i.e. a relative path was made absolute).  Leaves fallback values
    // that are already root-relative (/user/asset/…) untouched.
    if (resolved !== raw) {
      root.style.setProperty(varName, resolved);
    }
  }
}

// ── Strategy B helpers ────────────────────────────────────────────────────────

/** Extract every @font-face { ... } block from a CSS string. */
function extractFontFaces(cssText: string): string[] {
  const blocks: string[] = [];
  const re = /@font-face\s*\{[^}]*\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(cssText)) !== null) {
    blocks.push(m[0]);
  }
  return blocks;
}

/** Extract --image-<name>: <value>; declarations from a CSS string. */
function extractImageVars(cssText: string): Map<string, string> {
  const map = new Map<string, string>();
  const re  = /(--image-[\w-]+)\s*:\s*([^;]+);/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(cssText)) !== null) {
    const varName = m[1].trim();
    const value   = m[2].trim();
    if (!map.has(varName)) map.set(varName, value);
  }
  return map;
}

/**
 * Strategy B — fetch-based best-effort asset injection.
 *
 * Fetches the CSS text and:
 *   • Re-injects @font-face blocks with absolute src URLs.
 *   • Re-applies --image-* variables with resolved absolute URLs as inline
 *     styles (redundant if Strategy A already ran, but provides an extra safety
 *     net when the link fires its load event before Strategy A executes).
 *
 * Silently no-ops on CORS / network failure — Strategy A handles images, and
 * the browser loads @font-face directly from the linked stylesheet.
 */
export async function parseTenantCssAssets(cssHref: string): Promise<void> {
  if (typeof document === "undefined") return;

  let cssText: string;
  try {
    const res = await fetch(cssHref, { cache: "force-cache" });
    if (!res.ok) return;
    cssText = await res.text();
  } catch {
    return;
  }

  // @font-face — re-inject with absolute src URLs
  const fontFaces = extractFontFaces(cssText);
  if (fontFaces.length > 0) {
    const resolvedFonts = fontFaces
      .map((block) => resolveUrls(block, cssHref))
      .join("\n");

    const existing = document.getElementById("tenant-font-faces");
    if (existing) existing.remove();

    const style = document.createElement("style");
    style.id          = "tenant-font-faces";
    style.textContent = resolvedFonts;
    document.head.appendChild(style);
  }

  // --image-* vars — resolve and apply as inline styles (absolute URLs only)
  const imageVars = extractImageVars(cssText);
  if (imageVars.size > 0) {
    const root = document.documentElement;
    for (const [varName, value] of imageVars) {
      root.style.setProperty(varName, resolveUrls(value, cssHref));
    }
  }
}
